'use client';

/**
 * Client-side attributed-visit tracking. When a customer opens the catalog via a shared link
 * carrying `?utm_source=<channel>`, we record one visit per (store, source) per browser session
 * so the merchant's "Origem de visitas" report can attribute traffic to a channel.
 *
 * Server persistence lives in POST /api/track/visit.
 */

import { normalizeUtmCampaign, normalizeUtmSource } from '@/lib/utmSources';

const VISIT_GUARD_PREFIX = 'vupt-store-visit:';

const isBrowser = typeof window !== 'undefined';

/**
 * Record an attributed catalog visit once per (store, utmSource, utmCampaign) browser session.
 * Adding the campaign to the dedup key lets two campaigns on the same channel each count a visit.
 */
export function trackStoreVisit(
  storeId: string,
  rawUtmSource: string | null | undefined,
  rawUtmCampaign?: string | null
): void {
  if (!isBrowser || !storeId) return;
  const utmSource = normalizeUtmSource(rawUtmSource);
  if (!utmSource) return;
  const utmCampaign = normalizeUtmCampaign(rawUtmCampaign);

  const guardKey = `${VISIT_GUARD_PREFIX}${storeId}:${utmSource}:${utmCampaign ?? ''}`;
  try {
    if (window.sessionStorage.getItem(guardKey)) return;
    window.sessionStorage.setItem(guardKey, '1');
  } catch {
    /* no sessionStorage — fall through and still fire (best-effort) */
  }

  void fetch('/api/track/visit', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ storeId, utmSource, utmCampaign }),
    keepalive: true,
  }).catch(() => {
    /* tracking must never break the journey */
  });
}
